import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { ArrowDownToLine, ArrowUpFromLine, Layers, List, ChevronRight } from 'lucide-react';
import { formatINR, formatCarats, formatDateTime } from '@/lib/format';
import { isReceiptLedgerType, isIssueLedgerType } from '@/lib/ledgerTypes';
import MemoLink from '@/components/phase7/MemoLink';
import ReturnMemoLink from '@/components/phase7/ReturnMemoLink';
import { ledgerMergeHeading, formatLedgerDay } from '@/lib/ledgerMerge';

// Segmented Detailed / Merged ledger-view toggle (shared across the
// Diamonds / Colour Stones / Pearls packet ledgers).
export function PacketLedgerViewToggle({ mode, onChange, testId = 'packet-ledger-view-toggle' }) {
  return (
    <div className="inline-flex items-center rounded-md border border-[var(--divider)] overflow-hidden text-[11px] shadow-sm" role="group" aria-label="Ledger view" data-testid={testId}>
      <button type="button" onClick={() => onChange('detailed')} aria-pressed={mode === 'detailed'} className={`px-3 py-1.5 font-medium transition-colors ${mode === 'detailed' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid={`${testId}-detailed`}>
        <List size={12} className="inline mr-1 -mt-0.5" />Detailed
      </button>
      <button type="button" onClick={() => onChange('merged')} aria-pressed={mode === 'merged'} className={`px-3 py-1.5 font-medium transition-colors ${mode === 'merged' ? 'bg-[var(--ink-navy)] text-white' : 'bg-white text-slate-600 hover:bg-slate-50'}`} data-testid={`${testId}-merged`}>
        <Layers size={12} className="inline mr-1 -mt-0.5" />Merged
      </button>
    </div>
  );
}

// Particulars cell content for a single ledger entry — mirrors the detailed
// ledger row (minus the per-row edit kebab, which only lives in Detailed view).
export function LedgerParticulars({ e }) {
  let body;
  if (e.type === 'transfer_in') {
    body = (
      <span>
        {e.transfer_kind === 'recut'
          ? <>Received from <span className="text-amber-700 font-medium">Broken</span> <span className="font-mono">{e.counter_packet_no || 'broken packet'}</span>{e.counter_carats != null ? <span className="text-slate-500"> · re-cut {Number(e.counter_carats).toFixed(3)}→{Number(e.carats).toFixed(3)} ct</span> : null}</>
          : e.transfer_kind === 'break'
            ? <>Received from <span className="font-mono">{e.counter_packet_no || 'source packet'}</span> <span className="text-rose-700 text-[10px]">(break)</span></>
            : <>Received from <span className="font-medium font-mono">{e.counter_packet_no || 'another packet'}</span></>}
      </span>
    );
  } else if (e.type === 'transfer_out') {
    body = (
      <span>
        {e.transfer_kind === 'recut'
          ? <>Issued to <span className="font-mono">{e.counter_packet_no || 'destination'}</span> <span className="text-amber-700 text-[10px]">(re-cut)</span></>
          : e.transfer_kind === 'break'
            ? <>Issued to <span className="text-rose-700 font-medium">Broken</span> <span className="font-mono">{e.counter_packet_no || 'broken packet'}</span></>
            : <>Transferred to <span className="font-medium font-mono">{e.counter_packet_no || 'another packet'}</span></>}
      </span>
    );
  } else if (e.type === 'receipt') {
    body = (
      <span>
        {e.sub_type === 'goldsmith_return'
          ? <>Return from <span className="font-medium">{e.goldsmith_name || 'goldsmith'}</span>{e.order_no ? <> · <Link to={`/orders/${e.order_id}`} className="text-[var(--ink-navy)] hover:underline">{e.order_no}</Link></> : null}</>
          : <>From <span className="font-medium">{e.vendor_name || 'Unknown'}</span></>}
        {e.reference ? <span className="text-slate-500"> · {e.reference}</span> : null}
      </span>
    );
  } else if (e.issue_type === 'purchase_return') {
    body = <span>Purchase Return to <span className="font-medium">{e.vendor_name || 'vendor'}</span>{e.reference ? <span className="text-slate-500"> · {e.reference}</span> : null}</span>;
  } else if (e.issue_type === 'sale') {
    body = <span>Sale to <span className="font-medium">{e.sale_party_name || 'party'}</span>{e.reference ? <span className="text-slate-500"> · {e.reference}</span> : null}</span>;
  } else if (e.issue_type === 'return_to_vendor') {
    body = <span>Return to <span className="font-medium">{e.vendor_name || 'vendor'}</span>{e.reference ? <span className="text-slate-500"> · {e.reference}</span> : null}</span>;
  } else {
    body = (
      <span>
        To <span className="font-medium">{e.goldsmith_name || 'Unknown'}</span>
        {e.order_id && e.order_no && (
          <Link to={`/orders/${e.order_id}`} className="ml-1 text-[var(--ink-navy)] underline">· {e.order_no}</Link>
        )}
        {e.reference ? <span className="text-slate-500"> · {e.reference}</span> : null}
      </span>
    );
  }
  const hasMemo = e.issue_memo_no || e.return_memo_no || e.purchase_return_memo_no || e.sale_memo_no || e.vendor_return_memo_no;
  return (
    <>
      {body}
      {hasMemo && (
        <div className="mt-0.5 inline-flex gap-1 flex-wrap items-center">
          {e.issue_memo_no && <MemoLink id={e.goldsmith_issue_id} memoNo={e.issue_memo_no} testId={`ledger-merged-memo-${e.id}`} />}
          {e.return_memo_no && <ReturnMemoLink id={e.return_id} memoNo={e.return_memo_no} testId={`ledger-merged-return-memo-${e.id}`} />}
          {e.purchase_return_memo_no && <span className="inline-flex items-center text-[11px] font-mono bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded">{e.purchase_return_memo_no}</span>}
          {e.sale_memo_no && <span className="inline-flex items-center text-[11px] font-mono bg-emerald-50 text-emerald-800 border border-emerald-200 px-1.5 py-0.5 rounded">{e.sale_memo_no}</span>}
          {e.vendor_return_memo_no && !e.purchase_return_memo_no && <span className="inline-flex items-center text-[11px] font-mono bg-amber-50 text-amber-800 border border-amber-200 px-1.5 py-0.5 rounded">{e.vendor_return_memo_no}</span>}
        </div>
      )}
      {e.note ? <div className="text-slate-500">{e.note}</div> : null}
    </>
  );
}

// One merged group → a single clickable <tr>. Single-entry groups fall back to
// a normal detail row so the ledger still reads naturally.
function MergedRow({ group, canViewValues, onOpenGroup }) {
  const g = group;
  const e = g.entries.length === 1 ? g.entries[0] : null;

  if (e) {
    const isReceipt = isReceiptLedgerType(e.type);
    const isIssue = isIssueLedgerType(e.type);
    return (
      <tr data-testid={`ledger-row-${e.id}`}>
        <td className="whitespace-nowrap text-xs text-slate-600">{formatDateTime(e.timestamp)}</td>
        <td className="text-xs"><LedgerParticulars e={e} /></td>
        <td className="text-right font-mono tabular-nums ledger-group-sep">{isReceipt ? formatCarats(e.carats) : ''}</td>
        <td className="text-right font-mono tabular-nums">{isReceipt ? e.pieces : ''}</td>
        {canViewValues && <td className="text-right font-mono tabular-nums">{isReceipt ? formatINR(e.value) : ''}</td>}
        <td className="text-right font-mono tabular-nums ledger-group-sep">{isIssue ? formatCarats(e.carats) : ''}</td>
        <td className="text-right font-mono tabular-nums">{isIssue ? e.pieces : ''}</td>
        {canViewValues && <td className="text-right font-mono tabular-nums">{isIssue ? formatINR(e.value) : ''}</td>}
        <td className="text-right font-mono tabular-nums ledger-group-sep">{formatCarats(e.balance_carats)}</td>
        <td className="text-right font-mono tabular-nums">{e.balance_pieces}</td>
        {canViewValues && <td className="text-right font-mono tabular-nums">{formatINR(e.balance_value)}</td>}
      </tr>
    );
  }

  const rep = g.rep;
  const isReceipt = isReceiptLedgerType(rep.type);
  const isIssue = isIssueLedgerType(rep.type);
  const heading = ledgerMergeHeading(rep);
  return (
    <tr
      data-testid={`ledger-merged-row-${g.key}`}
      className="cursor-pointer hover:bg-amber-50/50 transition-colors"
      onClick={() => onOpenGroup(g)}
      role="button"
      tabIndex={0}
      onKeyDown={(ev) => { if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); onOpenGroup(g); } }}
      title="Click to view the individual entries merged into this row"
    >
      <td className="whitespace-nowrap text-xs font-medium text-[var(--ink-navy)]">{formatLedgerDay(g.day)}</td>
      <td className="text-xs">
        <div className="flex items-center gap-1.5 flex-wrap">
          {isReceipt
            ? <Badge className="bg-emerald-50 text-emerald-700 px-1.5 py-0"><ArrowDownToLine size={10} className="mr-0.5" />Receipt</Badge>
            : <Badge className="bg-rose-50 text-rose-700 px-1.5 py-0"><ArrowUpFromLine size={10} className="mr-0.5" />Issue</Badge>}
          <span>{heading.verb} <span className="font-medium">{heading.party}</span></span>
          <Badge variant="outline" className="border-amber-300 bg-amber-50 text-amber-800 px-1.5 py-0 gap-0.5"><Layers size={9} /> {g.entries.length} entries</Badge>
          <span className="inline-flex items-center text-slate-400"><ChevronRight size={13} /></span>
        </div>
        <span className="text-[11px] text-slate-500">Merged — click to view individual entries</span>
      </td>
      <td className="text-right font-mono tabular-nums ledger-group-sep font-semibold">{isReceipt ? formatCarats(g.total_carats) : ''}</td>
      <td className="text-right font-mono tabular-nums font-semibold">{isReceipt ? g.total_pieces : ''}</td>
      {canViewValues && <td className="text-right font-mono tabular-nums font-semibold">{isReceipt ? formatINR(g.total_value) : ''}</td>}
      <td className="text-right font-mono tabular-nums ledger-group-sep font-semibold">{isIssue ? formatCarats(g.total_carats) : ''}</td>
      <td className="text-right font-mono tabular-nums font-semibold">{isIssue ? g.total_pieces : ''}</td>
      {canViewValues && <td className="text-right font-mono tabular-nums font-semibold">{isIssue ? formatINR(g.total_value) : ''}</td>}
      <td className="text-right font-mono tabular-nums ledger-group-sep">{formatCarats(rep.balance_carats)}</td>
      <td className="text-right font-mono tabular-nums">{rep.balance_pieces}</td>
      {canViewValues && <td className="text-right font-mono tabular-nums">{formatINR(rep.balance_value)}</td>}
    </tr>
  );
}

// Rows for the Merged view — plug directly into the existing <tbody>.
export function MergedLedgerRows({ groups, canViewValues, onOpenGroup }) {
  return (
    <>
      {groups.map((g) => (
        <MergedRow key={g.key} group={g} canViewValues={canViewValues} onOpenGroup={onOpenGroup} />
      ))}
    </>
  );
}

// Breakdown dialog listing every entry merged into a grouped row.
export function MergedLedgerDialog({ group, canViewValues, onClose }) {
  const open = !!group;
  const ordered = group ? [...group.entries].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()) : [];
  const heading = group ? ledgerMergeHeading(group.rep) : null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="ledger-merged-detail-dialog">
        {group && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Layers size={16} className="text-amber-600" />
                {heading.verb} {heading.party}
              </DialogTitle>
              <DialogDescription>
                {group.entries.length} entries on {formatLedgerDay(group.day)} · merged into one row. Totals are the sum of these movements.
              </DialogDescription>
            </DialogHeader>
            <div className="rounded-md border border-[var(--divider)] overflow-x-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-600 uppercase text-[10px]">
                  <tr>
                    <th className="text-left px-2 py-2 font-medium">Time</th>
                    <th className="text-left px-2 py-2 font-medium">Particulars</th>
                    <th className="text-right px-2 py-2 font-medium">Cts</th>
                    <th className="text-right px-2 py-2 font-medium">Pcs</th>
                    {canViewValues && <th className="text-right px-2 py-2 font-medium">Value</th>}
                  </tr>
                </thead>
                <tbody>
                  {ordered.map((e) => (
                    <tr key={e.id} className="border-t border-[var(--divider)]" data-testid={`ledger-merged-detail-row-${e.id}`}>
                      <td className="px-2 py-2 align-top whitespace-nowrap text-slate-600">{formatDateTime(e.timestamp)}</td>
                      <td className="px-2 py-2 align-top"><LedgerParticulars e={e} /></td>
                      <td className="px-2 py-2 align-top text-right font-mono tabular-nums">{formatCarats(e.carats)}</td>
                      <td className="px-2 py-2 align-top text-right font-mono tabular-nums">{e.pieces}</td>
                      {canViewValues && <td className="px-2 py-2 align-top text-right font-mono tabular-nums">{formatINR(e.value)}</td>}
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-amber-200 bg-amber-50/60 font-semibold text-[var(--ink-navy)]">
                    <td className="px-2 py-2" colSpan={2}>Total ({group.entries.length} entries)</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums" data-testid="ledger-merged-total-carats">{formatCarats(group.total_carats)}</td>
                    <td className="px-2 py-2 text-right font-mono tabular-nums" data-testid="ledger-merged-total-pieces">{group.total_pieces}</td>
                    {canViewValues && <td className="px-2 py-2 text-right font-mono tabular-nums" data-testid="ledger-merged-total-value">{formatINR(group.total_value)}</td>}
                  </tr>
                </tfoot>
              </table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose} data-testid="ledger-merged-detail-close">Close</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
